import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Wiederverwendbarer Wrapper: jedes Dashboard-Section bekommt einen
// klickbaren Header mit Titel + Legende + Chevron. Zustand wird pro
// Section-Key in localStorage gemerkt, damit die Auswahl Browser-
// Refreshs überlebt.

type Props = {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  storageKey: string;       // localStorage-Key (eindeutig pro Section)
  defaultOpen?: boolean;
  // Optionale rechts neben dem Titel sichtbare Chips/Buttons
  // (Filter, Counter etc.) — bleiben auch im eingeklappten Zustand sichtbar.
  headerActions?: ReactNode;
  children: ReactNode;
};

function readStored(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(`v2.section.${key}`);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch { /* localStorage unavailable */ }
  return fallback;
}

function writeStored(key: string, value: boolean) {
  try { localStorage.setItem(`v2.section.${key}`, value ? '1' : '0'); } catch { /* ignore */ }
}

export function CollapsibleSection({
  title, subtitle, icon, storageKey, defaultOpen = true, headerActions, children,
}: Props) {
  const [open, setOpen] = useState(() => readStored(storageKey, defaultOpen));

  function toggle() {
    setOpen(o => {
      const next = !o;
      writeStored(storageKey, next);
      return next;
    });
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={toggle}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>{icon}</Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.4 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerActions && (
          <Box onClick={(e) => e.stopPropagation()}>
            {headerActions}
          </Box>
        )}
        <IconButton size="small" tabIndex={-1}>
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 180ms ease',
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={open} timeout={180} unmountOnExit={false}>
        <Box sx={{ p: 2, pt: 0 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
